import { useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/auth-context'
import { useLoadingSplash } from '../contexts/loading-splash-context'
import {
  isCaptureLive,
  useActiveSessionRecording,
} from '../features/session-recording/hooks/use-active-session-recording'
import { useAndroidAppUpdate } from '../hooks/use-android-app-update'
import { useVoiceCallSession } from '../hooks/use-voice-call-session'
import { isTerminalCallPhase } from '../stores/voice-call-store'
import { isUpdatePromptSuppressed } from '../utils/android-update-prompt-policy'
import { AndroidUpdateModal } from './android-update-modal'

const AUTH_ROUTE_PREFIXES = ['/sign-in', '/auth']

function useUpdatePromptSuppressed(): boolean {
  const { user, isLoading } = useAuth()
  const { pathname } = useLocation()
  const { isLoadingSplashVisible } = useLoadingSplash()
  const callSession = useVoiceCallSession()
  const recording = useActiveSessionRecording()
  return isUpdatePromptSuppressed({
    authLoading: isLoading,
    signedIn: Boolean(user),
    onAuthRoute: AUTH_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix)),
    splashVisible: isLoadingSplashVisible,
    // `elsewhere` is live on another of this user's devices: nothing local to cover.
    callActive:
      !!callSession && !isTerminalCallPhase(callSession.phase) && callSession.phase !== 'elsewhere',
    recordingActive: recording.ref !== null || isCaptureLive(recording.snapshot),
  })
}

/** Mounts the soft Android Play update check + dismissible modal. */
export function AndroidUpdateGate() {
  const suppressed = useUpdatePromptSuppressed()
  const { prompt, busy, dismiss, updateNow } = useAndroidAppUpdate({ suppressed })
  return (
    <AndroidUpdateModal
      prompt={prompt}
      busy={busy}
      onUpdate={() => void updateNow()}
      onDismiss={dismiss}
    />
  )
}
